import { Box } from '@mui/material';

// import: assets
import { ReactComponent as CopyDetail } from 'core/assets/svg/modal-icons/copy-detail.svg';
import BaseImage from 'core/components/image/BaseImage';
import BaseTypography from 'core/components/typography/BaseTypography';
import { aspectRatioConfig } from 'core/config/AspectRatioConfig';
import { NOT_APPLICABLE_DATA_VALUE } from 'core/constants/constants';

// import: constants
import { LayoutOrientation } from 'core/constants/enums/LayoutOrientation.ts';
import { ReusedComponentIDs } from 'core/constants/enums/ReusedComponentIDs.ts';
import { ToastType } from 'core/constants/enums/ToastTypeEnum.ts';
import { sendToast } from 'core/layouts/components/toast/ToastUtils';
import ActionsMenu from 'apps/studio/studio-tab/components/actions-menu/ActionsMenu';
import ImageInfoBottomBar from 'apps/studio/studio-tab/components/image-info/components/ImageInfoBottomBar';
import { imageInfoStyles } from 'apps/studio/studio-tab/components/image-info/ImageInfoStyles';

// import: components
import ShowMoreComponent from 'apps/studio/studio-tab/components/ShowMoreComponent';
import { colorPalette } from 'core/layouts/ui-blocks/ColorPalette';

// import: styles
import { reusableStyles } from 'core/layouts/ui-blocks/ReusableStyles';
import React from 'react';
import { useSelector } from 'react-redux';
import { modelsSelector } from 'core/store/reducers/studioReducer/StudioSlice';

// imports: utils
import { copyToClipboard, formatNumberString } from 'core/utils/utils';

export const styles = imageInfoStyles;
export default function ModelInfo({
	selectedPrompt,
	handleClose,
	feedPromptsModalType,
	hidePrompt,
	setHidePrompt,
	hideNegativePrompt,
	setHideNegativePrompt,
}) {
	const models = useSelector(modelsSelector);
	const isModelPresent = Object.values(models).some(
		(model) => model.label === selectedPrompt.modelLabel,
	);
	const details = [
		{
			label: 'Negative prompt',
			valueKey: 'negativePrompt',
		},
		{
			label: 'Sampler',
			valueKey: 'sampler',
			singleLine: true,
		},
		{
			label: 'Steps',
			valueKey: 'steps',
			singleLine: true,
		},
		{
			label: 'AI Guidance',
			valueKey: 'guidance',
			singleLine: true,
		},
		{
			label: 'Seed',
			valueKey: 'seed',
			singleLine: true,
		},
		{
			label: 'Aspect ratio',
			valueKey: 'aspectRatio',
			allowCopy: false,
			singleLine: true,
		},
	];

	return (
		<Box id="details-cont" sx={styles.promptDetailsCont}>
			<Box
				id="details-scroll-wrapper"
				sx={[
					styles.promptDetailsScrollWrapper,
					reusableStyles.verticalScrollBarStyles,
					{
						'&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
						'&::-webkit-scrollbar-thumb': { border: `4px solid ${colorPalette.dark6}` },
					},
				]}
			>
				<Box id="prompt-details" sx={styles.detailScreenPromptDetails}>
					<Box id={ReusedComponentIDs.PROMPT_MODAL_SCROLL_IDHAR} />
					<ActionsMenu
						image={selectedPrompt?.media}
						orientation={LayoutOrientation.HORIZONTAL}
						handleClose={handleClose}
						imageId={selectedPrompt?.generationId}
					/>
					<Box sx={[styles.detailCard]}>
						<Box id="model-used" sx={[reusableStyles.flexCol]}>
							<Box
								sx={[
									reusableStyles.flexRow,
									{
										width: '100%',
										marginBottom: '6px',
										justifyContent: 'space-between',
									},
								]}
							>
								<BaseTypography variant="b1_medium" styles={{ color: 'colors.light2' }}>
									Model used
								</BaseTypography>

								<Box sx={[reusableStyles.flexRow, { gap: '12px' }]}>
									<Box sx={[reusableStyles.flexRow, { gap: '4px' }]}>
										<Box sx={styles.detailCardModelImage}>
											<BaseImage src={selectedPrompt.modelImage} alt={selectedPrompt.modelLabel} />
										</Box>
										<BaseTypography variant="b2_caption" styles={{ color: 'colors.light2' }}>
											{selectedPrompt.modelLabel}
										</BaseTypography>
									</Box>
								</Box>
							</Box>
						</Box>
					</Box>

					<Box sx={[styles.detailCard]}>
						<Box id="prompt" sx={[reusableStyles.flexCol]}>
							<Box
								sx={[
									reusableStyles.flexRow,
									{
										width: '100%',
										marginBottom: '6px',
										justifyContent: 'space-between',
									},
								]}
							>
								<BaseTypography variant="b1_medium" styles={{ color: 'colors.light2' }}>
									Prompt
								</BaseTypography>

								<Box sx={[reusableStyles.flexRow, { gap: '12px' }]}>
									<Box
										sx={styles.copyDetailButton}
										onClick={(e) => {
											e.stopPropagation();
											copyToClipboard(selectedPrompt.prompt);
											sendToast(ToastType.VALUE_COPIED);
										}}
									>
										<CopyDetail />
									</Box>
								</Box>
							</Box>
							<Box sx={[styles.detailValue]}>
								<BaseTypography variant="b1_regular" styles={{ color: 'colors.light3' }}>
									{selectedPrompt && (
										<ShowMoreComponent
											hide={hidePrompt}
											value={selectedPrompt.prompt ?? ''}
											showMoreFunc={() => setHidePrompt(false)}
										/>
									)}
								</BaseTypography>
							</Box>
						</Box>
					</Box>

					<Box sx={[styles.detailCard]}>
						{details.map((detail, index) => {
							let icon = null;
							let value = selectedPrompt ? selectedPrompt[detail?.valueKey] : '';

							if (selectedPrompt && detail?.valueKey === 'aspectRatio') {
								icon = aspectRatioConfig[selectedPrompt.aspectRatioType]?.icon;
								value = aspectRatioConfig[selectedPrompt.aspectRatioType]?.label;
							}
							if (selectedPrompt && detail?.valueKey === 'negativePrompt') {
								value = (
									<ShowMoreComponent
										hide={hideNegativePrompt}
										value={selectedPrompt.negativePrompt ?? ''}
										showMoreFunc={() => setHideNegativePrompt(false)}
									/>
								);
							}

							let singleLine = false;
							if (detail.singleLine !== undefined) singleLine = detail?.singleLine;

							let copyIcon = true;
							if (detail.allowCopy !== undefined) copyIcon = detail?.allowCopy;

							const detailValueComp = (
								<Box sx={[styles.detailValue]}>
									{icon && icon}
									<BaseTypography variant="b1_regular" styles={{ color: 'colors.light3' }}>
										{selectedPrompt &&
											(detail?.valueKey === 'guidance'
												? value
													? formatNumberString(value)
													: NOT_APPLICABLE_DATA_VALUE
												: value || NOT_APPLICABLE_DATA_VALUE)}
									</BaseTypography>
								</Box>
							);

							return (
								<React.Fragment key={detail?.label}>
									{index !== 0 && <Box id="prompt-details-divider" sx={styles.divider} />}
									<Box id={detail?.label} sx={[reusableStyles.flexCol]}>
										<Box
											sx={[
												reusableStyles.flexRow,
												{
													width: '100%',
													marginBottom: '6px',
													justifyContent: 'space-between',
												},
											]}
										>
											<BaseTypography variant="b1_medium" styles={{ color: 'colors.light2' }}>
												{detail?.label}
											</BaseTypography>

											<Box sx={[reusableStyles.flexRow, { gap: '12px' }]}>
												{singleLine && detailValueComp}
												{copyIcon && (
													<Box
														sx={styles.copyDetailButton}
														onClick={(e) => {
															e.stopPropagation();
															sendToast(ToastType.VALUE_COPIED);
															copyToClipboard(selectedPrompt[detail?.valueKey]);
														}}
													>
														<CopyDetail />
													</Box>
												)}
											</Box>
										</Box>
										{!singleLine && detailValueComp}
									</Box>
								</React.Fragment>
							);
						})}
					</Box>
				</Box>
			</Box>
			<ImageInfoBottomBar
				selectedPrompt={selectedPrompt}
				handleClose={handleClose}
				feedPromptsModalType={feedPromptsModalType}
				showPublishPrompt={false}
				showReusePrompt={isModelPresent}
			/>
		</Box>
	);
}
